import { Fragment, useCallback, useMemo, useState } from "react";
import { Box, Text } from "@radix-ui/themes";
import {
  DropdownMenuCheckboxItem,
  DropdownMenuIcon,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  FilterMenuOption,
  isFilterMenuItemOption,
  isFilterMenuItemOptionGroup,
} from "../FilterMenu.model";
import styles from "../FilterMenu.module.scss";

function filterOptions(
  options: FilterMenuOption[],
  search: string
): FilterMenuOption[] {
  const normalizedSearch = search.trim().toLowerCase();

  if (normalizedSearch === "") {
    return options;
  }

  const matchingOptions = options.reduce<FilterMenuOption[]>((filteredOptions, option) => {
    if (isFilterMenuItemOptionGroup(option)) {
      const matchingOptions = option.options.filter(({ label }) =>
        label.toLowerCase().includes(normalizedSearch)
      );

      if (matchingOptions.length > 0) {
        filteredOptions.push({ ...option, options: matchingOptions });
      }
    } else if (option.label.toLowerCase().includes(normalizedSearch)) {
      filteredOptions.push(option);
    }

    return filteredOptions;
  }, []);

  return matchingOptions;
}

interface MenuOptionsProps {
  options: FilterMenuOption[];
  selected?: string | string[];
  showCheckboxes: boolean;
  keepOpenOnSelect: boolean;
  onOptionClick: (value: string) => void;
}

function MenuOptions({
  options,
  selected,
  showCheckboxes,
  keepOpenOnSelect,
  onOptionClick,
}: MenuOptionsProps) {
  return (
    <>
      {options.map((option) => {
        if (isFilterMenuItemOptionGroup(option) && option.options.length > 0) {
          return (
            <Fragment key={option.label}>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                {option.label}
              </DropdownMenuLabel>

              <MenuOptions
                options={option.options}
                selected={selected}
                showCheckboxes={showCheckboxes}
                keepOpenOnSelect={keepOpenOnSelect}
                onOptionClick={onOptionClick}
              />
            </Fragment>
          );
        }

        if (!isFilterMenuItemOption(option)) {
          return null;
        }

        if (
          showCheckboxes ||
          keepOpenOnSelect ||
          (selected != null && selected.length > 0)
        ) {
          return (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={selected?.includes(option.value)}
              onCheckedChange={() => onOptionClick(option.value)}
              onSelect={
                keepOpenOnSelect ? (event) => event.preventDefault() : undefined
              }
            >
              {option.icon && (
                <DropdownMenuIcon>{option.icon}</DropdownMenuIcon>
              )}
              {option.label}
            </DropdownMenuCheckboxItem>
          );
        }

        return (
          <DropdownMenuItem
            key={option.value}
            onClick={() => onOptionClick(option.value)}
          >
            {option.icon && (
              <DropdownMenuIcon>{option.icon}</DropdownMenuIcon>
            )}
            {option.label}
          </DropdownMenuItem>
        );
      })}
    </>
  );
}

interface MultiFilterMenuOptionsProps {
  label: string;
  options: FilterMenuOption[];
  selected?: string | string[];
  searchable?: boolean;
  showCheckboxes?: boolean;
  keepOpenOnSelect?: boolean;
  onOptionClick: (value: string) => void;
}

export function MultiFilterMenuOptions({
  label,
  options,
  selected,
  searchable = false,
  showCheckboxes = false,
  keepOpenOnSelect = false,
  onOptionClick,
}: MultiFilterMenuOptionsProps) {
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(
    () => filterOptions(options, search),
    [options, search]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    []
  );

  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.stopPropagation();
      }
    },
    []
  );

  return (
    <>
      {searchable && (
        <Box px="1" pt="1">
          <Input
            autoFocus
            className="mb-1"
            placeholder={`Search ${label.toLowerCase()}...`}
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
        </Box>
      )}

      <MenuOptions
        options={filteredOptions}
        selected={selected}
        showCheckboxes={showCheckboxes}
        keepOpenOnSelect={keepOpenOnSelect}
        onOptionClick={onOptionClick}
      />

      {filteredOptions.length === 0 && (
        <Box p="2">
          <Text color="gray" className={styles.emptyState}>
            No options found
          </Text>
        </Box>
      )}
    </>
  );
}
